/**
 * @file ArrangePlanTypesPage.js
 * @description Page for arranging the display order of plan types using drag and drop.
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import styles from './PlanYear.module.scss';
import {
    searchPlanTypes,
    updatePlanTypeOrder,
} from '../../services/benefitPlanService';
import { searchPlanYears } from '../../services/planYearService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
import { getEffectivePlanTypeSortOrder } from './_helpers/planTypeDisplayMode';

const ArrangePlanTypesPage = () => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();

    const [planTypes, setPlanTypes] = useState([]);
    const [originalPlanTypes, setOriginalPlanTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [selectedPlanYearId, setSelectedPlanYearId] = useState(null);

    useEffect(() => {
        const loadDefaultPlanYear = async () => {
            try {
                const response = await searchPlanYears(null, 1, 100);
                const years = response?.viewModels ?? [];
                const today = new Date().getTime();
                const current = years.find(y => {
                    const start = y.startDate
                        ? new Date(y.startDate).getTime()
                        : -Infinity;
                    const end = y.endDate
                        ? new Date(y.endDate).getTime()
                        : Infinity;
                    return today >= start && today <= end;
                });
                const fallback = [...years].sort((a, b) =>
                    String(b.startDate || '').localeCompare(a.startDate || ''),
                )[0];
                setSelectedPlanYearId((current ?? fallback)?.id ?? null);
            } catch (error) {
                console.error('Failed to load plan years', error);
            }
        };
        loadDefaultPlanYear();
    }, []);

    const loadPlanTypes = useCallback(async () => {
        if (!selectedPlanYearId) return;
        try {
            setIsLoading(true);
            const response = await searchPlanTypes(null, selectedPlanYearId);

            const fetchedPlanTypes = response || [];

            const sortedPlanTypes = fetchedPlanTypes.sort(
                (a, b) =>
                    getEffectivePlanTypeSortOrder(a) -
                    getEffectivePlanTypeSortOrder(b),
            );

            setPlanTypes(sortedPlanTypes);
            setOriginalPlanTypes(JSON.parse(JSON.stringify(sortedPlanTypes)));
        } catch (error) {
            console.error('Failed to fetch plan types', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_9c1b89f5f4a3'));
            }
        } finally {
            setIsLoading(false);
        }
    }, [selectedPlanYearId]);

    const checkForChanges = useCallback((currentPlanTypes, originalList) => {
        if (currentPlanTypes.length !== originalList.length) return true;

        return currentPlanTypes.some((planType, index) => {
            const original = originalList[index];
            return planType.id !== original.id;
        });
    }, []);

    useEffect(() => {
        loadPlanTypes();
    }, [loadPlanTypes]);

    useEffect(() => {
        setHasChanges(checkForChanges(planTypes, originalPlanTypes));
    }, [planTypes, originalPlanTypes, checkForChanges]);

    const handleDragStart = e => {
        const index = parseInt(e.target.dataset.index, 10);
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index);
        e.target.classList.add('dragging');
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDrop = e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;

        if (draggedIndex === null || targetIndex === null) {
            setDraggedIndex(null);
            setHoveredIndex(null);
            return;
        }

        let newIndex;
        if (draggedIndex < targetIndex) {
            newIndex = targetIndex - 1;
        } else {
            newIndex = targetIndex;
        }

        if (newIndex < 0) {
            newIndex = 0;
        }

        if (draggedIndex !== newIndex) {
            const newPlanTypes = [...planTypes];
            const [draggedPlanType] = newPlanTypes.splice(draggedIndex, 1);
            newPlanTypes.splice(newIndex, 0, draggedPlanType);

            const updatedPlanTypes = newPlanTypes.map((planType, index) => ({
                ...planType,
                sortOrder: index + 1,
            }));

            setPlanTypes(updatedPlanTypes);
        }

        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDragEnd = e => {
        e.target.classList.remove('dragging');
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDropTop = e => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== 0) {
            const newPlanTypes = [...planTypes];
            const [draggedPlanType] = newPlanTypes.splice(draggedIndex, 1);
            newPlanTypes.unshift(draggedPlanType);

            const updatedPlanTypes = newPlanTypes.map((planType, index) => ({
                ...planType,
                sortOrder: index + 1,
            }));

            setPlanTypes(updatedPlanTypes);
        }
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleReset = () => {
        setPlanTypes(JSON.parse(JSON.stringify(originalPlanTypes)));
        setHasChanges(false);
        Toast.info(getText('msg_de215a85a620'));
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const payload = planTypes.map((planType, index) => ({
                clientPlanTypeId: planType.id,
                sortOrder: index + 1,
            }));

            await updatePlanTypeOrder({ clientPlanTypeOrders: payload });

            setOriginalPlanTypes(JSON.parse(JSON.stringify(planTypes)));
            setHasChanges(false);
            Toast.success(getText('msg_52cc022c44e5'));
        } catch (error) {
            console.error('Failed to save plan type order', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_d1f73daa005d'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.PLAN_TYPES),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.PLAN_TYPES);
        }
    };

    const renderPlanTypes = () => {
        const items = [];

        items.push(
            <div
                key="top-drop-zone"
                className={`mb-3 d-flex align-items-center justify-content-center rounded ${
                    hoveredIndex === -1
                        ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-4'
                        : 'p-1'
                }`}
                onDragOver={handleDragOver}
                onDrop={handleDropTop}
                onDragEnter={() => setHoveredIndex(-1)}
                onDragLeave={() => setHoveredIndex(null)}
            >
                {hoveredIndex === -1 && (
                    <span className="text-muted">
                        <i className="bi bi-arrow-up me-2" />
                        {getText('msg_9dfd765eec6b')}
                    </span>
                )}
            </div>,
        );

        planTypes.forEach((planType, index) => {
            items.push(
                <div key={planType.id} className="mb-3">
                    <div className="row align-items-center">
                        <div className="col-12">
                            <div
                                className={`card shadow-sm rounded-3 ${styles.cardVisible} ${
                                    hoveredIndex === index
                                        ? 'border-primary border-2'
                                        : ''
                                }`}
                                draggable
                                data-index={index}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                onDragEnter={() => setHoveredIndex(index)}
                                onDragLeave={() => setHoveredIndex(null)}
                            >
                                <div className="card-body pe-0 py-2">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="bi bi-grip-vertical fs-5 text-muted" />
                                        </div>
                                        <div className="d-flex flex-grow-1 align-items-center">
                                            <h5 className="card-title mb-0">
                                                {planType.name ||
                                                    planType.planType?.name ||
                                                    'Unknown Plan Type'}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
            );

            items.push(
                <div
                    key={`drop-zone-${index}`}
                    className={`mb-2 d-flex align-items-center justify-content-center rounded ${
                        hoveredIndex === index + 1
                            ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-3'
                            : 'p-1'
                    }`}
                    data-index={index + 1}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={() => setHoveredIndex(index + 1)}
                    onDragLeave={() => setHoveredIndex(null)}
                >
                    {hoveredIndex === index + 1 && (
                        <span className="text-muted small">
                            <i className="bi bi-arrow-down me-2" />
                            {getText('msg_7c67e6fb70f3')}
                        </span>
                    )}
                </div>,
            );
        });

        return items;
    };

    if (isLoading) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageFill}>
            <div className={`${styles.pageContentFill} page-content`}>
                <div className={`${styles.cardFill} card`}>
                    <div className={`${styles.flexGrow1} card-body px-0 py-4`}>
                        <div className="ms-3 me-4 d-flex justify-content-between align-items-center py-3">
                            <div className="d-flex flex-column align-items-start mb-4">
                                <p className="page-title card-title ms-4 mb-0">
                                    {getText('msg_35a092cd8026')}
                                </p>
                                <p
                                    className={`ing-portals-subtitle mt-1 ${styles.planTypesSubtitle}`}
                                >
                                    {getText('msg_fcdfd878fb7b')}
                                </p>
                            </div>
                            <div className="float-end d-flex align-items-center align-self-baseline">
                                <span
                                    type="button"
                                    onClick={handleReset}
                                    className={`ing-text-buttons mx-3 ${hasChanges ? '' : `opacity-50 ${styles.actionDisabled}`}`}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_44c57abd888a')}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleSave}
                                    className={`ing-text-buttons me-3 d-flex ${hasChanges ? '' : `opacity-50 ${styles.actionDisabled}`}`}
                                >
                                    {isSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            {getText('msg_ae7e887517b0')}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check me-2" />
                                            {getText('msg_efc007a393f6')}
                                        </>
                                    )}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons me-3 d-flex"
                                    tabIndex={12}
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </span>
                            </div>
                        </div>

                        {planTypes.length === 0 ? (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_619956050f78')}
                                description={getText('msg_42d2f02acc1f')}
                            />
                        ) : (
                            <div className="container-fluid px-4">
                                <div className="row justify-content-center">
                                    <div className="col-12 col-lg-8 mt-4">
                                        {renderPlanTypes()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArrangePlanTypesPage;
